package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/**
 * One bordered group: the action, a hairline, and the ways of doing it differently.
 *
 * The group is `inline-flex` and the two controls inside it give up the radius and the border they
 * would otherwise draw where they meet, so the pair reads as one control with a seam rather than as
 * two buttons that happen to be touching. That seam is [LSplitButtonDividerStyle] — a real 1px
 * element and not a shared border, because on a `PRIMARY` split button both halves are the accent
 * colour and a border between them would be invisible.
 */
val LSplitButtonStyle = CssStyle.base {
    Modifier
        .styleModifier { property("display", "inline-flex") }
        .alignItems(AlignItems.Stretch)
}

/** The action. Square on the side the menu trigger is on. */
val LSplitButtonPrimaryStyle = CssStyle.base {
    Modifier.styleModifier {
        property("border-start-end-radius", "0")
        property("border-end-end-radius", "0")
        property("border-inline-end-width", "0")
    }
}

/**
 * The seam.
 *
 * `currentColor` would have been shorter and is wrong: the divider is not inside either button, so
 * it inherits the *group's* colour, which is the page's text colour and not the button's foreground.
 * The composable sets the colour from the variant instead, which is the one place that knows whether
 * this pair is painted on accent or on surface.
 */
val LSplitButtonDividerStyle = CssStyle.base {
    Modifier
        .width(1.px)
        .styleModifier { property("flex", "0 0 auto") }
}

/** The trigger. Square on the side the action is on. */
val LSplitButtonTriggerStyle = CssStyle.base {
    Modifier.styleModifier {
        property("border-start-start-radius", "0")
        property("border-end-start-radius", "0")
        property("border-inline-start-width", "0")
    }
}

/** The menu's positioning root, stretched so its trigger is as tall as the action beside it. */
val LSplitButtonMenuStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "inline-flex")
        property("align-items", "stretch")
    }
}

/**
 * A primary action with its alternatives behind a chevron.
 *
 * **The point is that the common case stays one press.** A menu of five equally-weighted commands
 * makes the reader choose before they can act; a split button states the choice the product would
 * make — "Pre-translate 412 untranslated" — and keeps the other four one press further away. That
 * is the same rule as one primary action per screen, applied inside a control.
 *
 * **The trigger has its own accessible name, and it is not "Open menu".** Every split button on a
 * page would then have the same one, so a listener moving through the page hears the same two words
 * repeatedly with nothing to say which action they belong to. The default names the action instead —
 * "More ways to pre-translate 412 untranslated" — which is long to read and exactly right to hear.
 * Pass [menuLabel] where a surface can say it better.
 *
 * [onSelect] receives the index into [items], per [LMenu]'s contract: separators and group titles
 * count, so `items[index]` is always the entry that was pressed.
 */
@Composable
fun LSplitButton(
    label: String,
    onClick: () -> Unit,
    items: List<LMenuEntry>,
    onSelect: (Int) -> Unit,
    modifier: Modifier = Modifier,
    variant: LButtonVariant = LButtonVariant.PRIMARY,
    size: LButtonSize = LButtonSize.MD,
    enabled: Boolean = true,
    loading: Boolean = false,
    menuLabel: String? = null,
    leadingIcon: (@Composable () -> Unit)? = null,
) {
    var expanded by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()
    // The two filled variants are painted on the accent, so their seam has to be drawn in the
    // foreground colour those buttons use; the two unfilled ones sit on the page and take the
    // hairline every other control boundary in the product takes.
    val dividerColor = when (variant) {
        LButtonVariant.PRIMARY, LButtonVariant.DANGER -> palette.accentContrast
        LButtonVariant.SECONDARY, LButtonVariant.GHOST -> palette.borderStrong
    }
    val dividerOpacity = when (variant) {
        LButtonVariant.PRIMARY, LButtonVariant.DANGER -> 0.32
        LButtonVariant.SECONDARY, LButtonVariant.GHOST -> 1.0
    }

    Div(LSplitButtonStyle.toModifier().then(modifier).toAttrs()) {
        LButton(
            label = label,
            onClick = onClick,
            modifier = LSplitButtonPrimaryStyle.toModifier(),
            variant = variant,
            size = size,
            enabled = enabled,
            leadingIcon = leadingIcon,
            loading = loading,
        )
        Div(
            LSplitButtonDividerStyle.toModifier()
                .backgroundColor(dividerColor)
                .opacity(dividerOpacity)
                .toAttrs { attr("aria-hidden", "true") },
        )
        LMenu(
            expanded = expanded,
            entries = items,
            onExpandedChange = { expanded = it },
            onSelect = onSelect,
            modifier = LSplitButtonMenuStyle.toModifier(),
            placement = LMenuPlacement.BOTTOM_END,
        ) { toggle, triggerModifier ->
            LIconButton(
                label = menuLabel ?: tf(
                    "ds.ds.lsplit_button.more_ways_to_label",
                    "More ways to {label}",
                    "label" to label,
                ),
                onClick = toggle,
                modifier = triggerModifier.then(LSplitButtonTriggerStyle.toModifier()),
                variant = variant,
                size = size,
                // Not `enabled && !loading`. The alternatives are how a reader answers a primary
                // action that is taking too long — cancel it, run the memory-only version, look at
                // the job — and closing that door for the duration of the thing they want to get
                // out of is the wrong instinct.
                enabled = enabled,
            ) {
                LChevronDownIcon(Modifier.iconSize(size.iconSize))
            }
        }
    }
}
